import React, { useState } from 'react';
import {
    Box,
    Popover,
    Typography,
    Button,
    useTheme,
    Divider,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Chip,
    Slider,
    IconButton
} from '@mui/material';
import { FilterList, Close } from '@mui/icons-material';
import { BasicAutocomplete } from './BasicAutocomplete';
import { STOCK_LEVELS } from '../config/constants';

export interface ProductFilterConfig {
    // Stock filters
    stockStatus: 'all' | 'in-stock' | 'low-stock' | 'out-of-stock';
    lowStockThreshold: number;
    
    // Price filters
    priceMin: number | null;
    priceMax: number | null;
    
    // Discount filters
    discountMin: number | null;
    discountMax: number | null;
    
    // Category filter
    categories: string[];
    
    // Variant filter
    variantStatus: 'all' | 'with-variants' | 'no-variants';
    
    // Status filter
    activeStatus: 'all' | 'active' | 'inactive';
}

interface ProductFiltersProps {
    filters: ProductFilterConfig;
    onChange: (filters: ProductFilterConfig) => void;
    availableCategories: string[];
}

const defaultFilters: ProductFilterConfig = {
    stockStatus: 'all',
    lowStockThreshold: STOCK_LEVELS.MEDIUM_THRESHOLD,
    priceMin: null,
    priceMax: null,
    discountMin: null,
    discountMax: null,
    categories: [],
    variantStatus: 'all',
    activeStatus: 'all'
};

export const ProductFilters: React.FC<ProductFiltersProps> = ({
    filters,
    onChange,
    availableCategories
}) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [tempFilters, setTempFilters] = useState<ProductFilterConfig>(filters);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setTempFilters(filters);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleApply = () => {
        onChange(tempFilters);
        handleClose();
    };

    const handleReset = () => {
        setTempFilters(defaultFilters);
        onChange(defaultFilters);
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (filters.stockStatus !== 'all') count++;
        if (filters.lowStockThreshold !== 30) count++;
        if (filters.priceMin !== null || filters.priceMax !== null) count++;
        if (filters.discountMin !== null || filters.discountMax !== null) count++;
        if (filters.categories.length > 0) count++;
        if (filters.variantStatus !== 'all') count++;
        if (filters.activeStatus !== 'all') count++;
        return count;
    };

    const activeCount = getActiveFilterCount();
    const open = Boolean(anchorEl);

    return (
        <>
            <Button
                onClick={handleClick}
                size='small'
                endIcon={<FilterList fontSize='small' />}
                sx={{
                    backgroundColor: theme.palette.mode === 'dark'
                        ? activeCount > 0
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(255, 255, 255, 0.08)'
                        : activeCount > 0
                            ? 'rgba(0, 0, 0, 0.12)'
                            : 'rgba(0, 0, 0, 0.04)',
                    color: theme.palette.text.primary,
                    fontSize: '0.75rem',
                    fontFamily: 'primaryFont',
                    px: 1.5,
                    py: 0.5,
                    minWidth: 'auto',
                    textTransform: 'none',
                    borderRadius: 1.5,
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(255, 255, 255, 0.16)'
                            : 'rgba(0, 0, 0, 0.12)',
                    },
                    position: 'relative'
                }}
            >
                Advanced Filter
                {activeCount > 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.9)'
                                : theme.palette.text.primary,
                            borderRadius: '50%',
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Typography sx={{
                            fontSize: '0.6rem',
                            color: theme.palette.mode === 'dark'
                                ? theme.palette.background.paper
                                : 'white',
                            fontWeight: 'bold'
                        }}>
                            {activeCount}
                        </Typography>
                    </Box>
                )}
            </Button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 420,
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                {/* Sticky Header with Actions */}
                <Box sx={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: theme.palette.background.paper,
                    zIndex: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    px: 3,
                    py: 2
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FilterList
                                fontSize='small'
                                sx={{ color: theme.palette.text.secondary }}
                            />
                            <Typography variant='subtitle1' sx={{ fontFamily: 'primaryFont', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                Product Filters
                            </Typography>
                        </Box>

                        {/* Action Buttons - Top Right */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                onClick={handleReset}
                                size='small'
                                variant='contained'
                                sx={{
                                    fontSize: '0.7rem',
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontFamily: 'primaryFont',
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255, 255, 255, 0.08)'
                                        : 'rgba(0, 0, 0, 0.08)',
                                    color: theme.palette.text.primary,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.12)'
                                            : 'rgba(0, 0, 0, 0.12)',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                Reset
                            </Button>
                            <Button
                                onClick={handleApply}
                                size='small'
                                variant='contained'
                                sx={{
                                    fontSize: '0.7rem',
                                    px: 1.5,
                                    py: 0.5,
                                    minWidth: 'auto',
                                    textTransform: 'none',
                                    fontFamily: 'primaryFont'
                                }}
                            >
                                Apply
                            </Button>
                            <IconButton size='small' onClick={handleClose} sx={{ ml: 0.5 }}>
                                <Close fontSize='small' />
                            </IconButton>
                        </Box>
                    </Box>
                </Box>

                {/* Scrollable Content */}
                <Box sx={{
                    overflowY: 'auto',
                    p: 3,
                    flex: 1
                }}>

                    {/* Stock Status */}
                    <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }}>
                        <FormLabel component='legend' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                            Stock Status
                        </FormLabel>
                        <RadioGroup
                            value={tempFilters.stockStatus}
                            onChange={(e) => setTempFilters({ ...tempFilters, stockStatus: e.target.value as any })}
                        >
                            <FormControlLabel
                                value='all'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='All Products'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='in-stock'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='In Stock'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='low-stock'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Low Stock'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='out-of-stock'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Out of Stock'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </RadioGroup>
                        
                        <Box sx={{ mt: 2 }}>
                            <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'primaryFont', mb: 1, display: 'block', fontSize: '0.7rem' }}>
                                Low Stock Threshold: {tempFilters.lowStockThreshold} units
                            </Typography>
                            <Slider
                                value={tempFilters.lowStockThreshold}
                                onChange={(_, value) => setTempFilters({ ...tempFilters, lowStockThreshold: value as number })}
                                min={0}
                                max={100}
                                step={5}
                                marks={[
                                    { value: 0, label: '0' },
                                    { value: 50, label: '50' },
                                    { value: 100, label: '100' }
                                ]}
                                valueLabelDisplay='auto'
                                size='small'
                                sx={{
                                    color: theme.palette.text.secondary,
                                    '& .MuiSlider-thumb': {
                                        backgroundColor: theme.palette.text.primary,
                                    },
                                    '& .MuiSlider-track': {
                                        backgroundColor: theme.palette.text.secondary,
                                    },
                                    '& .MuiSlider-rail': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : 'rgba(0, 0, 0, 0.1)',
                                    },
                                }}
                            />
                        </Box>
                    </FormControl>

                    <Divider sx={{ mb: 3 }} />

                    {/* Price Range */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.8rem' }}>
                            Price Range
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label='Min Price'
                                type='number'
                                size='small'
                                value={tempFilters.priceMin ?? ''}
                                onChange={(e) => setTempFilters({ ...tempFilters, priceMin: e.target.value ? parseFloat(e.target.value) : null })}
                                fullWidth
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                sx={{
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                }}
                            />
                            <TextField
                                label='Max Price'
                                type='number'
                                size='small'
                                value={tempFilters.priceMax ?? ''}
                                onChange={(e) => setTempFilters({ ...tempFilters, priceMax: e.target.value ? parseFloat(e.target.value) : null })}
                                fullWidth
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                sx={{
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Discount Range */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.8rem' }}>
                            Discount Range (%)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label='Min %'
                                type='number'
                                size='small'
                                value={tempFilters.discountMin ?? ''}
                                onChange={(e) => setTempFilters({ ...tempFilters, discountMin: e.target.value ? parseFloat(e.target.value) : null })}
                                fullWidth
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                sx={{
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                }}
                            />
                            <TextField
                                label='Max %'
                                type='number'
                                size='small'
                                value={tempFilters.discountMax ?? ''}
                                onChange={(e) => setTempFilters({ ...tempFilters, discountMax: e.target.value ? parseFloat(e.target.value) : null })}
                                fullWidth
                                InputLabelProps={{ sx: { fontSize: '0.8rem' } }}
                                sx={{
                                    '& input[type=number]': { MozAppearance: 'textfield' },
                                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 }
                                }}
                            />
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Categories */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant='caption' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1.5, display: 'block', fontSize: '0.8rem' }}>
                            Categories
                        </Typography>
                        <BasicAutocomplete
                            multiple
                            name='categories'
                            label='Select Categories'
                            fullWidth
                            size='small'
                            options={availableCategories}
                            value={tempFilters.categories}
                            onChange={(newValue) => setTempFilters({ ...tempFilters, categories: Array.isArray(newValue) ? newValue : [] })}
                        />
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Variant Status */}
                    <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }}>
                        <FormLabel component='legend' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                            Variant Status
                        </FormLabel>
                        <RadioGroup
                            value={tempFilters.variantStatus}
                            onChange={(e) => setTempFilters({ ...tempFilters, variantStatus: e.target.value as any })}
                        >
                            <FormControlLabel
                                value='all'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='All Products'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='with-variants'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='With Variants'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='no-variants'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='No Variants'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </RadioGroup>
                    </FormControl>

                    <Divider sx={{ mb: 3 }} />

                    {/* Active Status */}
                    <FormControl component='fieldset' sx={{ mb: 3, width: '100%' }}>
                        <FormLabel component='legend' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, fontSize: '0.8rem' }}>
                            Active Status
                        </FormLabel>
                        <RadioGroup
                            value={tempFilters.activeStatus}
                            onChange={(e) => setTempFilters({ ...tempFilters, activeStatus: e.target.value as any })}
                        >
                            <FormControlLabel
                                value='all'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='All Products'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='active'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Active Only'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                            <FormControlLabel
                                value='inactive'
                                control={<Radio size='small' sx={{ color: theme.palette.text.secondary, '&.Mui-checked': { color: theme.palette.text.primary } }} />}
                                label='Inactive Only'
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                            />
                        </RadioGroup>
                    </FormControl>

                    {/* Active Filters Summary */}
                    {activeCount > 0 && (
                        <>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ mb: 2 }}>
                                <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'primaryFont', fontWeight: 600, mb: 1, display: 'block', fontSize: '0.75rem' }}>
                                    Active Filters ({activeCount})
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {filters.stockStatus !== 'all' && (
                                        <Chip
                                            label={`Stock: ${filters.stockStatus}`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, stockStatus: 'all' })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {filters.categories.length > 0 && (
                                        <Chip
                                            label={`${filters.categories.length} categories`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, categories: [] })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {filters.variantStatus !== 'all' && (
                                        <Chip
                                            label={`Variants: ${filters.variantStatus}`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, variantStatus: 'all' })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                    {filters.activeStatus !== 'all' && (
                                        <Chip
                                            label={`Status: ${filters.activeStatus}`}
                                            size='small'
                                            onDelete={() => setTempFilters({ ...tempFilters, activeStatus: 'all' })}
                                            sx={{ fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>
            </Popover>
        </>
    );
};

